import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, AlertTriangle, Users, ClipboardCheck,
  FileWarning, BarChart3, Menu, X, ChevronRight, Building2, ExternalLink,
  Shield, Package, Truck, Send, DollarSign, Layers, UserPlus, CalendarDays, FileText, Warehouse,
  FileCheck, UserCheck, Boxes, Megaphone, Settings, ClipboardList, Target, Receipt, Bell,
  Lightbulb, Wrench, Database, ShoppingCart, ShieldCheck, Award,
  LogOut, User, Moon, Sun, HelpCircle, Send as SendIcon, Zap, TrendingUp,
  Map, Clock, Sparkles, Square, Eye, Phone, Banknote,
  RefreshCw,
  Activity,
} from "lucide-react";
import { GlobalCallButton } from "@/components/GlobalCallPanel";
import { GlobalFrictionButton } from "@/components/GlobalFrictionButton";
import { GlobalMessengerButton } from "@/components/GlobalMessengerButton";
import { SceneDirectorBar } from "@/components/cinematic/SceneDirectorBar";
import { NarratorPanel } from "@/components/cinematic/NarratorPanel";
import { useCinematic } from "@/data/cinematicMode/context";
import { useMaverick } from "@/data/maverick/context";
import { MaverickLayout } from "@/components/maverick/MaverickLayout";
import { MaverickHandoffBanner } from "@/components/maverick/MaverickHandoffBanner";
import { cn } from "@/lib/utils";
import React, { useState, useEffect, useMemo } from "react";
import { RoleSwitcher } from "@/components/RoleSwitcher";
import { AlertNotificationCenter } from "@/components/AlertNotificationCenter";
import { ChatNotificationBell } from "@/components/ChatNotificationBell";
import { useRole, UserRole } from "@/data/RoleContext";
import { RoleRouteGuard } from "@/components/RoleRouteGuard";
import { useBranding } from "@/data/BrandingContext";
import { getCatalogPermissions } from "@/data/catalogPermissions";
import { TenantSwitcher } from "@/components/TenantSwitcher";
import { SmartContextBanner } from "@/components/SmartContextBanner";
import { TenantIdentityBar } from "@/components/TenantIdentityBar";
import { useNavigationConfig, applyNavConfig } from "@/data/NavigationConfigContext";
import { useNavMode } from "@/data/NavModeContext";
import { useUISkin } from "@/data/UISkinContext";
import { CommandRailSidebar } from "@/components/CommandRailSidebar";
import { isRouteFoldedForRole } from "@/data/foldRegistry";
import { useSidebarMode } from "@/data/sidebarMode";
import { usePermissions } from "@/data/PermissionsContext";
import { isPermittedRoute } from "@/data/permissionsMapping";
import { isRouteAllowed } from "@/data/rolePermissions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAuth } from "@/contexts/AuthContext";
import {
  isSystemDrawerRoute,
  isDrawerOpenByDefault,
  getStoredDrawerOpen,
  setStoredDrawerOpen,
} from "@/data/systemDrawer";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useOptionalAppData } from "@/data/AppDataContext";
import { getTotalUnreadCount } from "@/services/team-message.service";
import { supabase } from "@/lib/supabase";
import { TeamMessagesSidebar } from "@/components/team-messages/TeamMessagesSidebar";

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  /** When true, only exact pathname match activates this item — no child-route matching. */
  exact?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const HIDDEN_LEFT_NAV_PATHS = new Set(["/execution/installers", "/contractor-intake"]);

function isHiddenLeftNavItem(item: Pick<NavItem, "path">): boolean {
  return HIDDEN_LEFT_NAV_PATHS.has(item.path);
}

function getAllItems(section: NavSection): NavItem[] {
  return section.items;
}

/**
 * Determine if a sidebar item should render as the active route.
 *
 * Uses path-segment matching, NOT raw startsWith — this prevents
 * `/scheduling-dispatch` from also lighting up the `/scheduling` item
 * (the bug that caused two menu entries to highlight at once).
 *
 * Active when:
 *   • current pathname exactly equals the item path, OR
 *   • current pathname is a child route of the item path
 *     (i.e. starts with `${itemPath}/`)
 */
function isPathActive(pathname: string, itemPath: string, exact?: boolean): boolean {
  // /contractor-portal is the observed-session URL alias for /installer-portal
  const resolved = pathname.startsWith("/contractor-portal") ? "/installer-portal" : pathname;
  if (itemPath === "/") return resolved === "/";
  if (resolved === itemPath) return true;
  if (exact) return false;
  return resolved.startsWith(itemPath + "/");
}

/* ─────────────────────────────────────────────
   NAV SECTIONS — Reorganized by business flow
   ───────────────────────────────────────────── */

function getNavSections(role: UserRole): NavSection[] {
  switch (role) {
    case "super_admin":
      return [
        {
          label: "Leadership",
          items: [
            { label: "Command Dashboard", path: "/command-dashboard", icon: Target },
            { label: "Reports", path: "/reports", icon: BarChart3 },
            { label: "Appointment Results", path: "/appointment-results", icon: FileCheck },
            { label: "Rep Scorecards", path: "/rep-scorecards", icon: Award },
          ],
        },
        {
          label: "Marketing",
          items: [
            { label: "Leads", path: "/growth-channels", icon: Megaphone },
            { label: "Lead Sources", path: "/lead-sources", icon: Megaphone },
            { label: "Lead Integrity", path: "/lead-integrity", icon: Shield },
            { label: "Pipeline", path: "/pipeline", icon: Target },
            { label: "Marketing Attribution", path: "/marketing-attribution", icon: BarChart3 },
          ],
        },
        {
          label: "Sales",
          items: [
            { label: "Projects", path: "/projects", icon: Briefcase },
            { label: "Appointments", path: "/appointments", icon: CalendarDays },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Pre-Construction",
          items: [
            { label: "Build Review", path: "/intake-review", icon: ClipboardCheck },
            { label: "Permit Tracker", path: "/permitting", icon: FileText },
          ],
        },
        {
          label: "Operations",
          items: [
            { label: "Schedules", path: "/scheduling", icon: CalendarDays },
            { label: "Ordering", path: "/ordering-queue", icon: ShoppingCart },
            { label: "Warehouse", path: "/warehouse", icon: Warehouse },
          ],
        },
        {
          label: "Execution",
          items: [
            { label: "Live Ops", path: "/execution/installers", icon: LayoutDashboard },
            { label: "Offers", path: "/installer-offers", icon: Activity },
            { label: "Progress", path: "/execution", icon: CalendarDays, exact: true },
            { label: "Resolve", path: "/resolve", icon: AlertTriangle },
          ],
        },
        {
          label: "Network",
          items: [
            { label: "Installers", path: "/contractors", icon: Users },
            { label: "Intake", path: "/contractor-intake", icon: ClipboardList },
            { label: "Onboarding", path: "/contractor-onboarding", icon: UserPlus },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Install QA", path: "/qa-form", icon: ClipboardCheck },
            { label: "Report Issue", path: "/issue-form", icon: FileWarning },
          ],
        },
        {
          label: "Financials",
          items: [
            { label: "Accounting", path: "/accounting", icon: DollarSign },
            { label: "Revenue Intelligence", path: "/revenue-intelligence", icon: TrendingUp },
            { label: "Earnings Intelligence", path: "/earnings-intelligence", icon: TrendingUp },
          ],
        },
        {
          label: "Automation",
          items: [
            { label: "Automations", path: "/automations", icon: Zap },
            { label: "Follow-Up Engine", path: "/follow-up-engine", icon: Zap },
          ],
        },
        {
          label: "Reports",
          items: [
            { label: "Disposition Report", path: "/disposition-report", icon: FileText },
          ],
        },
        {
          label: "Catalog",
          items: [
            { label: "Offerings", path: "/offerings", icon: Layers },
            { label: "Product Registry", path: "/product-registry", icon: Database },
          ],
        },
        {
          label: "Admin / System",
          items: [
            { label: "Portal Hub", path: "/portals", icon: ExternalLink },
            { label: "Migration Review", path: "/import/review-queue", icon: ClipboardList },
            { label: "Lead / Appointment Import", path: "/import/leads-appointments", icon: CalendarDays },
            { label: "Control Plane", path: "/control-plane", icon: Shield },
            { label: "Navigation Settings", path: "/navigation-settings", icon: Settings },
            { label: "Settings", path: "/settings", icon: Settings },
            { label: "Checklist Templates", path: "/checklist-templates", icon: ClipboardCheck },
            { label: "Demo Lab", path: "/demo-lab", icon: Lightbulb },
          ],
        },
      ];

    case "owner":
      return [
        {
          label: "Leadership",
          items: [
            { label: "Command Dashboard", path: "/command-dashboard", icon: Target },
            { label: "Reports", path: "/reports", icon: BarChart3 },
            { label: "Appointment Results", path: "/appointment-results", icon: FileCheck },
            { label: "Rep Scorecards", path: "/rep-scorecards", icon: Award },
          ],
        },
        {
          label: "Marketing",
          items: [
            { label: "Pipeline", path: "/pipeline", icon: Target },
            { label: "Marketing Attribution", path: "/marketing-attribution", icon: BarChart3 },
          ],
        },
        {
          label: "Sales",
          items: [
            { label: "Projects", path: "/projects", icon: Briefcase },
            { label: "Appointments", path: "/appointments", icon: CalendarDays },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Execution",
          items: [
            { label: "Schedules", path: "/scheduling", icon: CalendarDays },
            { label: "Live Ops", path: "/execution/installers", icon: LayoutDashboard },
            { label: "Offers", path: "/installer-offers", icon: Activity },
            { label: "Progress", path: "/execution", icon: CalendarDays, exact: true },
            { label: "Resolve", path: "/resolve", icon: AlertTriangle },
          ],
        },
        {
          label: "Network",
          items: [
            { label: "Installers", path: "/contractors", icon: Users },
            { label: "Intake", path: "/contractor-intake", icon: ClipboardList },
            { label: "Onboarding", path: "/contractor-onboarding", icon: UserPlus },
          ],
        },
        {
          label: "Procurement",
          items: [
            { label: "Ordering", path: "/ordering-queue", icon: ShoppingCart },
            { label: "Warehouse", path: "/warehouse", icon: Warehouse },
          ],
        },
        {
          label: "Financials",
          items: [
            { label: "Accounting", path: "/accounting", icon: DollarSign },
            { label: "Revenue Intelligence", path: "/revenue-intelligence", icon: TrendingUp },
            { label: "Earnings Intelligence", path: "/earnings-intelligence", icon: TrendingUp },
          ],
        },
        {
          label: "Reports",
          items: [
            { label: "Disposition Report", path: "/disposition-report", icon: FileText },
          ],
        },
        {
          label: "Admin / System",
          items: [
            { label: "Portal Hub", path: "/portals", icon: ExternalLink },
            { label: "Lead / Appointment Import", path: "/import/leads-appointments", icon: CalendarDays },
            { label: "Control Plane", path: "/control-plane", icon: Shield },
            { label: "Settings", path: "/settings", icon: Settings },
            { label: "Checklist Templates", path: "/checklist-templates", icon: ClipboardCheck },
          ],
        },
      ];

    case "ops_manager":
      return [
        {
          label: "Marketing",
          items: [
            { label: "Leads", path: "/growth-channels", icon: Megaphone },
            { label: "Lead Sources", path: "/lead-sources", icon: Megaphone },
            { label: "Lead Integrity", path: "/lead-integrity", icon: Shield },
            { label: "Pipeline", path: "/pipeline", icon: Target },
            { label: "Marketing Attribution", path: "/marketing-attribution", icon: BarChart3 },
          ],
        },
        {
          label: "Sales",
          items: [
            { label: "Projects", path: "/projects", icon: Briefcase },
            { label: "Appointments", path: "/appointments", icon: CalendarDays },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Execution",
          items: [
            { label: "Schedules", path: "/scheduling", icon: CalendarDays },
            { label: "Live Ops", path: "/execution/installers", icon: LayoutDashboard },
            { label: "Offers", path: "/installer-offers", icon: Activity },
            { label: "Progress", path: "/execution", icon: CalendarDays, exact: true },
            { label: "Resolve", path: "/resolve", icon: AlertTriangle },
          ],
        },
        {
          label: "Network",
          items: [
            { label: "Installers", path: "/contractors", icon: Users },
            { label: "Intake", path: "/contractor-intake", icon: ClipboardList },
            { label: "Onboarding", path: "/contractor-onboarding", icon: UserPlus },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Install QA", path: "/qa-form", icon: ClipboardCheck },
            { label: "Report Issue", path: "/issue-form", icon: FileWarning },
          ],
        },
        {
          label: "Procurement",
          items: [
            { label: "Ordering", path: "/ordering-queue", icon: ShoppingCart },
            { label: "Permitting", path: "/permitting", icon: FileText },
            { label: "Warehouse", path: "/warehouse", icon: Warehouse },
          ],
        },
        {
          label: "Finance",
          items: [
            { label: "Accounting", path: "/accounting", icon: DollarSign },
          ],
        },
        {
          label: "Automation",
          items: [
            { label: "Automations", path: "/automations", icon: Zap },
          ],
        },
        {
          label: "Reports",
          items: [
            { label: "Reports", path: "/reports", icon: BarChart3 },
            { label: "Disposition Report", path: "/disposition-report", icon: FileText },
          ],
        },
        {
          label: "Admin / System",
          items: [
            { label: "Portal Hub", path: "/portals", icon: ExternalLink },
            { label: "Migration Review", path: "/import/review-queue", icon: ClipboardList },
            { label: "Lead / Appointment Import", path: "/import/leads-appointments", icon: CalendarDays },
          ],
        },
      ];

    case "it_staff":
    case "admin":
      return [
        {
          label: "Leadership",
          items: [
            { label: "Command Dashboard", path: "/command-dashboard", icon: Target },
            { label: "Reports", path: "/reports", icon: BarChart3 },
          ],
        },
        {
          label: "Marketing",
          items: [
            { label: "Leads", path: "/growth-channels", icon: Megaphone },
            { label: "Lead Sources", path: "/lead-sources", icon: Megaphone },
            { label: "Lead Integrity", path: "/lead-integrity", icon: Shield },
            { label: "Pipeline", path: "/pipeline", icon: Target },
            { label: "Marketing Attribution", path: "/marketing-attribution", icon: BarChart3 },
          ],
        },
        {
          label: "Sales",
          items: [
            { label: "Projects", path: "/projects", icon: Briefcase },
            { label: "Appointments", path: "/appointments", icon: CalendarDays },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Execution",
          items: [
            { label: "Schedules", path: "/scheduling", icon: CalendarDays },
            { label: "Live Ops", path: "/execution/installers", icon: LayoutDashboard },
            { label: "Offers", path: "/installer-offers", icon: Activity },
            { label: "Progress", path: "/execution", icon: CalendarDays, exact: true },
            { label: "Resolve", path: "/resolve", icon: AlertTriangle },
          ],
        },
        {
          label: "Network",
          items: [
            { label: "Installers", path: "/contractors", icon: Users },
            { label: "Intake", path: "/contractor-intake", icon: ClipboardList },
            { label: "Onboarding", path: "/contractor-onboarding", icon: UserPlus },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Install QA", path: "/qa-form", icon: ClipboardCheck },
            { label: "Report Issue", path: "/issue-form", icon: FileWarning },
          ],
        },
        {
          label: "Procurement",
          items: [
            { label: "Ordering", path: "/ordering-queue", icon: ShoppingCart },
            { label: "Permitting", path: "/permitting", icon: FileText },
            { label: "Warehouse", path: "/warehouse", icon: Warehouse },
          ],
        },
        {
          label: "Finance",
          items: [
            { label: "Accounting", path: "/accounting", icon: DollarSign },
          ],
        },
        {
          label: "Automation",
          items: [
            { label: "Automations", path: "/automations", icon: Zap },
            { label: "Follow-Up Engine", path: "/follow-up-engine", icon: Zap },
          ],
        },
        {
          label: "Reports",
          items: [
            { label: "Disposition Report", path: "/disposition-report", icon: FileText },
          ],
        },
        {
          label: "Catalog",
          items: [
            { label: "Offerings", path: "/offerings", icon: Layers },
            { label: "Product Registry", path: "/product-registry", icon: Database },
          ],
        },
        {
          label: "Admin / System",
          items: [
            { label: "Portal Hub", path: "/portals", icon: ExternalLink },
            { label: "Migration Review", path: "/import/review-queue", icon: ClipboardList },
            { label: "Lead / Appointment Import", path: "/import/leads-appointments", icon: CalendarDays },
            { label: "Settings", path: "/settings", icon: Settings },
            { label: "Checklist Templates", path: "/checklist-templates", icon: ClipboardCheck },
          ],
        },
      ];

    case "installer":
      return [
        {
          label: "Jobs",
          items: [
            { label: "My Jobs", path: "/", icon: Briefcase },
            { label: "Installer Portal", path: "/installer-portal", icon: LayoutDashboard },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Install QA", path: "/qa-form", icon: ClipboardCheck },
            { label: "Report Issue", path: "/issue-form", icon: FileWarning },
          ],
        },
      ];

    case "subcontractor":
      return [
        {
          label: "Jobs",
          items: [
            { label: "Opportunities", path: "/", icon: Send },
            { label: "My Jobs", path: "/jobs", icon: Briefcase },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Install QA", path: "/qa-form", icon: ClipboardCheck },
            { label: "Report Issue", path: "/issue-form", icon: FileWarning },
          ],
        },
      ];

    case "warehouse_manager":
      return [
        {
          label: "Jobs",
          items: [
            { label: "Dashboard", path: "/", icon: LayoutDashboard },
            { label: "Jobs", path: "/jobs", icon: Briefcase },
          ],
        },
        {
          label: "Procurement",
          items: [
            { label: "Warehouse", path: "/warehouse", icon: Warehouse },
            { label: "Ordering", path: "/ordering-queue", icon: ShoppingCart },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Issues", path: "/issues", icon: AlertTriangle },
            { label: "Service Tickets", path: "/service-tickets", icon: Wrench },
            { label: "Report Issue", path: "/issue-form", icon: FileWarning },
          ],
        },
      ];

    case "cfo":
      return [
        {
          label: "Finance",
          items: [
            { label: "Command Center", path: "/", icon: LayoutDashboard },
            { label: "Revenue Intelligence", path: "/revenue-intelligence", icon: TrendingUp },
            { label: "Earnings Intelligence", path: "/earnings-intelligence", icon: TrendingUp },
            { label: "Accounting", path: "/accounting", icon: DollarSign },
            { label: "Commissions", path: "/commissions", icon: Receipt },
          ],
        },
        {
          label: "Jobs",
          items: [
            { label: "Jobs", path: "/jobs", icon: Briefcase },
            { label: "Opportunities", path: "/opportunities", icon: Target },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Reporting",
          items: [
            { label: "Reports", path: "/reports", icon: BarChart3 },
            { label: "Alerts History", path: "/alerts", icon: Bell },
          ],
        },
      ];

    case "finance_manager":
      return [
        {
          label: "Finance",
          items: [
            { label: "Command Center", path: "/", icon: LayoutDashboard },
            { label: "Revenue Intelligence", path: "/revenue-intelligence", icon: TrendingUp },
            { label: "Earnings Intelligence", path: "/earnings-intelligence", icon: TrendingUp },
            { label: "Accounting", path: "/accounting", icon: DollarSign },
            { label: "Commissions", path: "/commissions", icon: Receipt },
          ],
        },
        {
          label: "Jobs",
          items: [
            { label: "Jobs", path: "/jobs", icon: Briefcase },
            { label: "Opportunities", path: "/opportunities", icon: Target },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Reporting",
          items: [
            { label: "Reports", path: "/reports", icon: BarChart3 },
            { label: "Alerts History", path: "/alerts", icon: Bell },
          ],
        },
      ];

    case "bookkeeper":
      return [
        {
          label: "Financials",
          items: [
            { label: "Command Center", path: "/", icon: LayoutDashboard },
            { label: "Accounting", path: "/accounting", icon: DollarSign },
          ],
        },
        {
          label: "Jobs",
          items: [
            { label: "Jobs", path: "/jobs", icon: Briefcase },
            { label: "Opportunities", path: "/opportunities", icon: Target },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Reporting",
          items: [
            { label: "Reports", path: "/reports", icon: BarChart3 },
          ],
        },
      ];

    case "head_of_installation":
      return [
        {
          label: "Jobs",
          items: [
            { label: "Command Center", path: "/", icon: LayoutDashboard },
            { label: "Jobs", path: "/jobs", icon: Briefcase },
            { label: "Install Readiness", path: "/install-readiness", icon: ShieldCheck },
            { label: "Schedules", path: "/scheduling", icon: CalendarDays },
          ],
        },
        {
          label: "Execution",
          items: [
            { label: "Live Ops", path: "/execution/installers", icon: LayoutDashboard },
            { label: "Offers", path: "/installer-offers", icon: Activity },
            { label: "Progress", path: "/execution", icon: CalendarDays, exact: true },
            { label: "Resolve", path: "/resolve", icon: AlertTriangle },
          ],
        },
        {
          label: "Network",
          items: [
            { label: "Installers", path: "/contractors", icon: Users },
            { label: "Intake", path: "/contractor-intake", icon: ClipboardList },
            { label: "Onboarding", path: "/contractor-onboarding", icon: UserPlus },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Install QA", path: "/qa-form", icon: ClipboardCheck },
            { label: "Report Issue", path: "/issue-form", icon: FileWarning },
          ],
        },
        {
          label: "Procurement",
          items: [
            { label: "Ordering", path: "/ordering-queue", icon: ShoppingCart },
            { label: "Warehouse", path: "/warehouse", icon: Warehouse },
          ],
        },
      ];

    case "procurement_manager":
      return [
        {
          label: "Jobs",
          items: [
            { label: "Command Center", path: "/", icon: LayoutDashboard },
            { label: "Jobs", path: "/jobs", icon: Briefcase },
          ],
        },
        {
          label: "Procurement",
          items: [
            { label: "Ordering", path: "/ordering-queue", icon: ShoppingCart },
            { label: "Warehouse", path: "/warehouse", icon: Warehouse },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Issues", path: "/issues", icon: AlertTriangle },
            { label: "Service Tickets", path: "/service-tickets", icon: Wrench },
          ],
        },
      ];

    case "inventory_manager":
      return [
        {
          label: "Jobs",
          items: [
            { label: "Command Center", path: "/", icon: LayoutDashboard },
            { label: "Jobs", path: "/jobs", icon: Briefcase },
          ],
        },
        {
          label: "Procurement",
          items: [
            { label: "Warehouse", path: "/warehouse", icon: Warehouse },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Issues", path: "/issues", icon: AlertTriangle },
            { label: "Service Tickets", path: "/service-tickets", icon: Wrench },
          ],
        },
      ];

    case "sales":
      return [
        {
          label: "Marketing",
          items: [
            { label: "Pipeline", path: "/pipeline", icon: Target },
          ],
        },
        {
          label: "Sales",
          items: [
            { label: "Command Center", path: "/", icon: LayoutDashboard },
            { label: "Opportunities", path: "/opportunities", icon: Target },
            { label: "Appointments", path: "/appointments", icon: CalendarDays },
            { label: "Follow-Ups", path: "/follow-ups", icon: Clock },
            { label: "Appointment Results", path: "/appointment-results", icon: FileCheck },
            { label: "Rep Scorecards", path: "/rep-scorecards", icon: Award },
            { label: "Jobs", path: "/jobs", icon: Briefcase },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Quality & Service",
          items: [
            { label: "Report Issue", path: "/issue-form", icon: FileWarning },
          ],
        },
      ];

    case "sales_manager":
      return [
        {
          label: "Leadership",
          items: [
            { label: "Command Dashboard", path: "/command-dashboard", icon: Target },
            { label: "Reports", path: "/reports", icon: BarChart3 },
            { label: "Appointment Results", path: "/appointment-results", icon: FileCheck },
            { label: "Rep Scorecards", path: "/rep-scorecards", icon: Award },
          ],
        },
        {
          label: "Marketing",
          items: [
            { label: "Marketing Attribution", path: "/marketing-attribution", icon: BarChart3 },
          ],
        },
        {
          label: "Sales Oversight",
          items: [
            { label: "Command Center", path: "/", icon: LayoutDashboard },
            { label: "Projects", path: "/projects", icon: Briefcase },
            { label: "Appointments", path: "/appointments", icon: CalendarDays },
            { label: "Customers", path: "/customers", icon: UserCheck },
          ],
        },
        {
          label: "Operations",
          items: [
            { label: "Issues", path: "/issues", icon: AlertTriangle },
          ],
        },
        {
          label: "Reports",
          items: [
            { label: "Disposition Report", path: "/disposition-report", icon: FileText },
          ],
        },
      ];

    case "call_center":
      return [
        {
          label: "Leads Management",
          items: [
            { label: "Pipeline", path: "/pipeline", icon: Target },
            // Conversations hidden — surfaced inline on Customer Profile (route preserved)
            { label: "Follow-Ups", path: "/follow-ups", icon: Clock },
            { label: "Appointments", path: "/appointments", icon: CalendarDays },
          ],
        },
      ];

    default:
      return [];
  }
}

/* ─────────────────────────────────────────────
   Section icons — matches new section labels
   ───────────────────────────────────────────── */

const MODULE_ICONS: Record<string, React.ElementType> = {
  "Leadership": Target,
  "Marketing": Megaphone,
  "Leads Management": Target,
  "Leads": Target,
  "Sales": Send,
  "Sales Oversight": Send,
  "Pre-Construction": ClipboardCheck,
  "Materials & Procurement": Package,
  "Operations": LayoutDashboard,
  "Execution": Wrench,
  "Network": Users,
  "Contractors": Users,
  "Install": CalendarDays,
  "Progress": CalendarDays,
  "Live Ops": LayoutDashboard,
  "Installer Portal": Wrench,
  "Resolve": AlertTriangle,
  "Quality & Service": AlertTriangle,
  "Financials": DollarSign,
  "Intelligence": Lightbulb,
  "Automation": Zap,
  "Automation & Intelligence": Zap,
  "Reports": BarChart3,
  "Reporting": BarChart3,
  "Contractor Network": Users,
  "Portals": ExternalLink,
  "Catalog": Layers,
  "Admin / System": Settings,
  // Legacy labels for other roles
  "Pipeline": Target,
  "Jobs": Briefcase,
  "Quality & Issues": AlertTriangle,
  "Fulfillment": Package,
  "Reporting & Intelligence": BarChart3,
  "Finance": DollarSign,
  "Risk & Alerts": Bell,
  "Alerts": Bell,
};

/* ─────────────────────────────────────────────
   Detect if a section is the "Admin / System"
   block — rendered separately in footer area
   ───────────────────────────────────────────── */
const ADMIN_SECTION_LABEL = "Admin / System";

function toTitleCase(input: string): string {
  return input
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function dedupeRepeatedWords(input: string): string {
  const words = input
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const deduped: string[] = [];
  for (const word of words) {
    const prev = deduped[deduped.length - 1];
    if (!prev || prev.toLowerCase() !== word.toLowerCase()) {
      deduped.push(word);
    }
  }
  return deduped.join(" ");
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const appData = useOptionalAppData();
  const isRefreshing = appData?.isRefreshing ?? false;
  const lastRefreshedAt = appData?.lastRefreshedAt ?? null;
  const [syncNowTick, setSyncNowTick] = useState(0);
  useEffect(() => {
    if (!lastRefreshedAt) return;
    const id = window.setInterval(() => setSyncNowTick((v) => v + 1), 5_000);
    return () => window.clearInterval(id);
  }, [lastRefreshedAt]);

  const lastSyncedLabel = useMemo(() => {
    if (!appData) return "Last synced: unavailable";
    if (!lastRefreshedAt) return isRefreshing ? "Syncing..." : "Sync pending...";
    const ts = new Date(lastRefreshedAt).getTime();
    if (!Number.isFinite(ts)) return "Last synced: unknown";
    const diffMs = Math.max(0, Date.now() - ts);
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `Last synced: ${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `Last synced: ${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `Last synced: ${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `Last synced: ${day}d ago`;
  }, [appData, isRefreshing, lastRefreshedAt, syncNowTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [teamUnreadCount, setTeamUnreadCount] = useState(0);
  const [teamPanelOpen, setTeamPanelOpen] = useState(false);

  // Load real team message unread count and keep it live via realtime
  useEffect(() => {
    let cancelled = false;
    getTotalUnreadCount().then((n) => { if (!cancelled) setTeamUnreadCount(n); }).catch(() => {});

    const ch = supabase
      .channel(`applayoutt_team_badge:${Date.now()}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'team_messages' }, () => {
        getTotalUnreadCount().then((n) => { if (!cancelled) setTeamUnreadCount(n); }).catch(() => {});
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'team_message_reads' }, () => {
        getTotalUnreadCount().then((n) => { if (!cancelled) setTeamUnreadCount(n); }).catch(() => {});
      })
      .subscribe();

    return () => {
      cancelled = true;
      ch.unsubscribe();
    };
  }, []);
  const { role, accountRole, roleInfo } = useRole();
  const { hasPermission } = usePermissions();
  const isSuperAdmin = accountRole === "super_admin";
  const showRoleSwitcher = accountRole === "super_admin" || accountRole === "owner" || accountRole === "admin" || accountRole === "it_staff";
  const { user, profile } = useCurrentUser();
  const { signOut } = useAuth();
  const { state: cinematicState } = useCinematic();
  const { state: maverickState, toggleMaverick } = useMaverick();

  // Reset mobile menu on role change
  useEffect(() => {
    setMobileOpen(false);
  }, [role]);
  const { branding, setBranding } = useBranding();
  const darkMode = branding.darkMode;
  const catalogPerms = getCatalogPermissions(role);
  const { config: navConfig } = useNavigationConfig();
const { navMode, setNavMode } = useNavMode();
  const { skin, toggleSkin } = useUISkin();
  const { mode: sidebarMode } = useSidebarMode(role);
  const storedDrawerOpen = getStoredDrawerOpen();
  const [systemOpen, setSystemOpen] = useState<boolean>(
    storedDrawerOpen !== null ? storedDrawerOpen : isDrawerOpenByDefault(role),
  );
  const [viewAsOpen, setViewAsOpen] = useState<boolean>(() => {
  try {
    return localStorage.getItem("view-as-drawer-open") === "true";
  } catch {
    return false;
  }
});
const toggleViewAsDrawer = () => {
  setViewAsOpen((prev) => {
    const next = !prev;
    try {
      localStorage.setItem("view-as-drawer-open", String(next));
    } catch {
      // localStorage unavailable; ignore
    }
    return next;
  });
};
const userDisplayName = (() => {
  const profileName = String(profile?.full_name ?? profile?.name ?? "").trim();
  if (profileName) return profileName;
  const metadataName = String((user?.user_metadata as Record<string, unknown>)?.full_name ?? "").trim();
  if (metadataName) return metadataName;
  const email = String(user?.email ?? "").trim();
  if (!email) return "User";
  const localPart = email.split("@")[0] || "";
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "User";
})();
const userEmail = String(profile?.email ?? user?.email ?? "").trim() || "—";
const avatarInitials = getInitials(userDisplayName);
const normalizedRoleLabel = (() => {
  const fromRoleInfo = String(roleInfo?.label ?? "").trim();
  const fromRoleValue = String(role ?? "").trim();
  const base = fromRoleInfo || fromRoleValue || "User";
  return dedupeRepeatedWords(toTitleCase(base));
})();

  // ── Fold Plan (Phase 2) ──────────────────────────────────────
  // When sidebarMode === "new", remove folded routes from the
  // sidebar per role. Routes themselves remain live; users reach
  // them via the parent surface declared in foldRegistry.ts.
  // When sidebarMode === "legacy", the role sees the unfolded menu.
  const rawSections = getNavSections(role).filter(
    (section) => section.label !== "Catalog" || catalogPerms.view
  );
  const baseSections = rawSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          !isHiddenLeftNavItem(item) &&
          (isSuperAdmin || isPermittedRoute(hasPermission, item.path) || isRouteAllowed(role, item.path)),
      ),
    }))
    .filter((section) => getAllItems(section).length > 0);
  const defaultSections = sidebarMode === "new"
    ? baseSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !isRouteFoldedForRole(item.path, role)),
        }))
        .filter((section) => getAllItems(section).length > 0)
    : baseSections;

  // Cockpit consolidation:
  //   • Root-level Command Center links are hidden because that experience now
  //     lives inside Command Dashboard.
  //   • Marketing  — Growth Channels + Lead Sources collapse to "Leads"
  //                  (unified MarketingPerformanceCockpit at /growth-channels).
  //   • Execution  — Scheduling + Dispatch Board + Dispatch Intelligence collapse
  //                  to "Scheduling & Dispatch" (unified SchedulingDispatchCockpit
  //                  at /scheduling-dispatch).
  // Classic mode keeps every item intact.
  const consolidatedSections = skin === "cockpit"
    ? defaultSections
        .map(section => {
          const withoutCommandCenter = section.items.filter(
            item => !(item.path === "/" && item.label === "Command Center"),
          );
          const cockpitSection = withoutCommandCenter.length === section.items.length
            ? section
            : { ...section, items: withoutCommandCenter };

          // ── Marketing lens consolidation ─────────────────────────────
          if (cockpitSection.label === "Marketing") {
            const filtered = cockpitSection.items.filter(
              item => item.path !== "/growth-channels" && item.path !== "/lead-sources",
            );
            const hadEither = cockpitSection.items.some(
              item => item.path === "/growth-channels" || item.path === "/lead-sources",
            );
            if (!hadEither) return cockpitSection;
            return {
              ...cockpitSection,
              items: [
                { label: "Leads", path: "/growth-channels", icon: Megaphone },
                ...filtered,
              ],
            };
          }

          // ── Install lens consolidation (cockpit mode) ─────────────────
          // Execution section flat items — collapse scheduling/dispatch paths
          // into the single "/execution" hub entry.
          if (cockpitSection.label === "Execution") {
            const collapsedPaths = new Set(["/execution", "/installer-dashboard", "/resolve"]);
            const hadAny = cockpitSection.items.some(item => collapsedPaths.has(item.path));
            if (!hadAny) return cockpitSection;
            return cockpitSection;
          }

          return cockpitSection;
        })
        .filter(section => getAllItems(section).length > 0)
    : defaultSections;

  // Apply user's navigation customization
  const allSections = applyNavConfig(consolidatedSections, navConfig);

  // ── System Drawer (Phase 3) ──────────────────────────────────
  // Pull SYSTEM_DRAWER_ROUTES out of every section into a single
  // collapsed drawer at the bottom. The legacy "Admin / System"
  // section also rolls in here. Routes stay live; permissions
  // unchanged — we only relocate the link.
  const systemDrawerItems: NavItem[] = [];
  const seenSystemPaths = new Set<string>();
  const navSections = allSections
    .map((section) => {
      const kept: NavItem[] = [];
      for (const item of section.items) {
        const isSystem =
          isSystemDrawerRoute(item.path) || section.label === ADMIN_SECTION_LABEL;
        if (isSystem) {
          if (!seenSystemPaths.has(item.path)) {
            seenSystemPaths.add(item.path);
            systemDrawerItems.push(item);
          }
        } else {
          kept.push(item);
        }
      }
      return { ...section, items: kept };
    })
    // Drop the now-empty Admin / System section and any other empties.
    .filter((s) => getAllItems(s).length > 0 && s.label !== ADMIN_SECTION_LABEL);

  const toggleSystemDrawer = () => {
    setSystemOpen((prev) => {
      const next = !prev;
      setStoredDrawerOpen(next);
      return next;
    });
  };

  // Legacy variable kept for the mobile nav block below — now resolves
  // to the System drawer payload.
  const adminSection = systemDrawerItems.length > 0
    ? { label: "System", items: systemDrawerItems }
    : null;

  // Derive the active section + page label for the breadcrumb bar
  const activeNavItem = useMemo(() => {
    for (const section of navSections) {
      for (const item of getAllItems(section)) {
        if (isPathActive(location.pathname, item.path)) {
          return { section: section.label, page: item.label };
        }
      }
    }
    return null;
  }, [navSections, location.pathname]);

  if (maverickState.active) {
    return <MaverickLayout />;
  }

  return (
    <div
      className="min-h-screen flex flex-col bg-background"
      style={{ "--app-shell-sidebar-width": "220px" } as React.CSSProperties}
    >
      {/* Cinematic Scene Director Bar + Narrator */}
      <SceneDirectorBar />
      <NarratorPanel />
      <div className="flex flex-1 min-h-0">
      <RoleRouteGuard />
      {navMode === "command" ? (
        <CommandRailSidebar />
      ) : (
      <>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-[220px] flex-col bg-sidebar border-r border-sidebar-border shrink-0 sticky top-0 h-screen">
        {/* Brand */}
        <div className="px-5 py-4 border-b border-sidebar-border">
          <div className="flex items-center gap-2.5">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.companyName} className="h-7 w-7 rounded-lg object-contain" />
            ) : (
              <div className="h-7 w-7 rounded-lg bg-sidebar-primary flex items-center justify-center">
                <Shield className="h-3.5 w-3.5 text-sidebar-primary-foreground" />
              </div>
            )}
            <div className="flex items-baseline gap-1.5">
              <h1 className="text-[15px] font-bold text-sidebar-accent-foreground tracking-tight leading-none">
                {branding.platformName}
              </h1>
              <span className="text-[10px] font-semibold text-sidebar-primary tracking-[0.08em] leading-none px-1.5 py-0.5 rounded-sm bg-sidebar-primary/12 border border-sidebar-primary/25">
                OS v0.6
              </span>
            </div>
          </div>
        </div>

        {/* Tenant Context */}
        <div className="border-b border-sidebar-border">
          <TenantSwitcher />
        </div>

        {/* Module Navigation */}
        <nav className="flex-1 px-3 py-3 space-y-0 overflow-y-auto scrollbar-thin">
          {navSections.map((section, idx) => {
            const ModIcon = MODULE_ICONS[section.label];
            const hasActiveChild = getAllItems(section).some(
              (item) => isPathActive(location.pathname, item.path),
            );
            return (
              <div key={section.label} className={cn(idx > 0 && "pt-2.5 mt-1 border-t border-sidebar-border/40")}>
                {/* Category header */}
                <div className={cn(
                  "flex items-center gap-2 px-2.5 py-1 mb-0.5 rounded-md transition-colors",
                  hasActiveChild ? "text-sidebar-accent-foreground" : "text-sidebar-foreground/70"
                )}>
                  {ModIcon && <ModIcon className={cn("h-3 w-3 shrink-0", hasActiveChild ? "text-sidebar-primary" : "text-sidebar-foreground/55")} />}
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] leading-none">
                    {section.label}
                  </p>
                </div>
                {/* Flat items */}
                <div className="space-y-px pl-2">
                  {section.items.map((item) => {
                    const active = isPathActive(location.pathname, item.path, item.exact);
                    return (
                      <Link
                        key={item.path + item.label}
                        to={item.path}
                        className={cn(
                          "flex items-center gap-2.5 px-2.5 py-[6px] rounded-md text-[13px] font-medium transition-all",
                          active
                            ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm border-l-2 border-sidebar-primary"
                            : "text-sidebar-foreground/50 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground/80 border-l-2 border-transparent"
                        )}
                      >
                        <item.icon className={cn("h-3.5 w-3.5 shrink-0", active ? "text-sidebar-primary" : "")} />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}

        </nav>

        {/* Footer — Roles drawer (bypass roles only) */}
        {showRoleSwitcher && (
          <div className="border-t border-sidebar-border">
            <button
              type="button"
              onClick={toggleViewAsDrawer}
              aria-expanded={viewAsOpen}
              className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-sidebar-foreground/55 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Eye className="h-3.5 w-3.5" />
                Roles
                <span className="text-sidebar-foreground/35 font-semibold normal-case tracking-normal">
                  · {normalizedRoleLabel}
                </span>
              </span>
              {viewAsOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
            {viewAsOpen && (
              <div className="px-3 pb-2 max-h-64 overflow-y-auto scrollbar-thin">
                <RoleSwitcher />
              </div>
            )}
          </div>
        )}

        {/* Footer — System drawer (Phase 3) */}
        {adminSection && adminSection.items.length > 0 && (
          <div className="border-t border-sidebar-border">
            <button
              type="button"
              onClick={toggleSystemDrawer}
              aria-expanded={systemOpen}
              className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-sidebar-foreground/55 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Settings className="h-3.5 w-3.5" />
                System
                <span className="text-sidebar-foreground/35 font-semibold normal-case tracking-normal">
                  ({adminSection.items.length})
                </span>
              </span>
              {systemOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
            {systemOpen && (
              <div className="px-3 pb-2 space-y-0.5">
                {adminSection.items.map((item) => (
                  <Link
                    key={item.path + item.label}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-2.5 px-2.5 py-[7px] rounded-md text-[13px] font-medium transition-all",
                      location.pathname === item.path
                        ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
                        : "text-sidebar-foreground/55 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    )}
                  >
                    <item.icon className="h-3.5 w-3.5 shrink-0" />
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="px-3 py-2 border-t border-sidebar-border">
          <button
            onClick={() => setNavMode("command")}
            className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-[11px] font-medium text-sidebar-foreground/55 hover:text-sidebar-foreground hover:bg-sidebar-accent/40 transition-colors"
            title="Switch to Command Rail"
          >
            <span className="uppercase tracking-[0.12em]">Standard</span>
            <span className="text-sidebar-foreground/40">try rail →</span>
          </button>
        </div>
        <div className="px-3 py-2.5 border-t border-sidebar-border">
          <p className="text-[9px] text-sidebar-foreground/20 font-medium px-2">{branding.platformName} OS · v0.6-beta</p>
        </div>
      </aside>
      </>
      )}

      {/* Mobile */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Tenant Identity Bar */}
        <TenantIdentityBar />
        {/* Desktop top bar with alerts */}
        <div className="hidden md:flex items-center justify-end gap-3 px-6 py-2 border-b bg-card sticky top-0 z-40">
          {isRefreshing && (
            <div className="mr-auto inline-flex items-center gap-2 rounded-md border border-primary/25 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              Syncing latest data...
            </div>
          )}
          {!isRefreshing && (
            <div className="mr-auto text-[11px] text-muted-foreground">
              {lastSyncedLabel}
            </div>
          )}
          <button
            onClick={toggleMaverick}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors border border-primary/20"
          >
            <Zap className="h-3.5 w-3.5" />
            Maverick
          </button>
          {/* Team Messages icon — toggles floating sidebar */}
          <button
            onClick={() => setTeamPanelOpen((v) => !v)}
            className="relative p-2 text-muted-foreground hover:text-foreground transition-colors rounded-md"
            title="Team Messages"
          >
            <SendIcon className="h-4 w-4" />
            {teamUnreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center px-1 tabular-nums pointer-events-none">
                {teamUnreadCount > 99 ? '99+' : teamUnreadCount}
              </span>
            )}
          </button>
          <AlertNotificationCenter />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-3 border-l border-border hover:bg-muted/50 rounded-md px-2 py-1 transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-bold">{avatarInitials}</AvatarFallback>
                </Avatar>
                <span className="text-[12px] font-semibold leading-tight">{userDisplayName}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold">{userDisplayName}</p>
                  <p className="text-xs text-muted-foreground">{userEmail}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <User className="mr-2 h-4 w-4" /> View Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="mr-2 h-4 w-4" /> Account Settings
              </DropdownMenuItem>
              {showRoleSwitcher && (
                <DropdownMenuItem disabled className="opacity-60">
                  <Shield className="mr-2 h-4 w-4" /> Role: {normalizedRoleLabel}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setBranding({ ...branding, darkMode: !darkMode })}>
                {darkMode ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                {darkMode ? "Light Mode" : "Dark Mode"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleSkin}>
                {skin === "cockpit" ? <Square className="mr-2 h-4 w-4" /> : <Sparkles className="mr-2 h-4 w-4" />}
                <span className="flex-1">{skin === "cockpit" ? "Switch to Classic" : "Switch to Cockpit"}</span>
                <span className="text-[10px] text-muted-foreground ml-2 uppercase tracking-wider">{skin}</span>
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="opacity-60">
                <HelpCircle className="mr-2 h-4 w-4" /> Help & Support
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={signOut}>
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <header className="md:hidden flex items-center justify-between px-4 py-2.5 border-b bg-card sticky top-0 z-50">
          <div className="flex items-center gap-2">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.companyName} className="h-6 w-6 rounded-md object-contain" />
            ) : (
              <div className="h-6 w-6 rounded-md bg-primary flex items-center justify-center">
                <Shield className="h-3 w-3 text-primary-foreground" />
              </div>
            )}
            <div>
              <span className="text-[13px] font-bold text-foreground tracking-tight">
                {branding.platformName}
              </span>
              {showRoleSwitcher && (
                <span className="text-[9px] text-muted-foreground ml-2 hidden sm:inline">{normalizedRoleLabel}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isRefreshing && (
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                <RefreshCw className="h-2.5 w-2.5 animate-spin" />
                Syncing
              </span>
            )}
            {!isRefreshing && (
              <span className="text-[10px] text-muted-foreground hidden sm:inline">
                {lastSyncedLabel}
              </span>
            )}
            <ChatNotificationBell />
            {/* Team Messages icon — mobile */}
            <button
              onClick={() => setTeamPanelOpen((v) => !v)}
              className="relative p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-md"
              title="Team Messages"
            >
              <SendIcon className="h-4 w-4" />
              {teamUnreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center px-1 tabular-nums pointer-events-none">
                  {teamUnreadCount > 99 ? '99+' : teamUnreadCount}
                </span>
              )}
            </button>
            <AlertNotificationCenter />
            <button onClick={() => setMobileOpen(!mobileOpen)} className="p-1.5 rounded-md hover:bg-muted">
              {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </header>

        {mobileOpen && (
          <div className="md:hidden bg-card border-b p-3 space-y-2 sticky top-[45px] z-50 shadow-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/60 text-xs font-medium text-muted-foreground">
              <Building2 className="h-3.5 w-3.5" />
              {branding.companyName}
            </div>
            {navSections.map((section) => {
              const ModIcon = MODULE_ICONS[section.label];
              return (
                <div key={section.label}>
                  <div className="flex items-center gap-1.5 px-2 mb-1">
                    {ModIcon && <ModIcon className="h-2.5 w-2.5 text-muted-foreground/40" />}
                    <p className="text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-[0.14em]">
                      {section.label}
                    </p>
                  </div>
                  {section.items.map((item) => {
                    const active = isPathActive(location.pathname, item.path, item.exact);
                    return (
                      <Link
                        key={item.path + item.label}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs font-medium transition-colors",
                          active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                        )}
                      >
                        <item.icon className="h-3.5 w-3.5" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              );
            })}
            {adminSection && (
              <div>
                <div className="flex items-center gap-1.5 px-2 mb-1">
                  <Settings className="h-2.5 w-2.5 text-muted-foreground/40" />
                  <p className="text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-[0.14em]">
                    {adminSection.label}
                  </p>
                </div>
                {adminSection.items.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path + item.label}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs font-medium transition-colors",
                        active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
            {showRoleSwitcher && (
              <div className="pt-2 border-t">
                <button
                  type="button"
                  onClick={toggleViewAsDrawer}
                  aria-expanded={viewAsOpen}
                  className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70 hover:text-foreground transition-colors"
                >
                  <span className="flex items-center gap-1.5">
                    <Eye className="h-3 w-3" />
                    Roles · <span className="normal-case tracking-normal font-semibold">{normalizedRoleLabel}</span>
                  </span>
                  {viewAsOpen ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </button>
                {viewAsOpen && <RoleSwitcher />}
              </div>
            )}
          </div>
        )}

        <MaverickHandoffBanner />
        <SmartContextBanner />
        {/* Section breadcrumb bar — desktop only */}
        {(activeNavItem || (location.pathname.startsWith("/jobs/") && location.pathname.length > 6)) && (
          <div className="hidden md:flex items-center gap-2 px-6 py-2 border-b bg-card text-[12px]">
            {location.pathname.startsWith("/jobs/") && location.pathname.length > 6 ? (
              <>
                <Link to="/projects" className="text-foreground/70 font-medium hover:text-foreground transition-colors">Projects</Link>
                <ChevronRight className="h-3.5 w-3.5 text-foreground/40" />
                <Link to="/projects" className="text-foreground/70 font-medium hover:text-foreground transition-colors">Jobs</Link>
                <ChevronRight className="h-3.5 w-3.5 text-foreground/40" />
                <span className="text-foreground/70 font-medium">Detail</span>
              </>
            ) : isPathActive(location.pathname, "/projects") ? (
              <span className="text-foreground/70 font-medium">Pipeline Ops</span>
            ) : (
              <>
                <span className="text-foreground/70 font-medium">{activeNavItem!.section}</span>
                <ChevronRight className="h-3.5 w-3.5 text-foreground/40" />
                <span className="text-foreground/70 font-medium">
                  {location.pathname === "/intake-review" ? "Intake Review" : activeNavItem!.page}
                </span>
              </>
            )}
          </div>
        )}
        <main className={cn("flex-1 p-3 md:p-5 overflow-auto", skin === "cockpit" && "cockpit-surface")}>{children}</main>
        <GlobalMessengerButton />
        <GlobalFrictionButton />
        <TeamMessagesSidebar
          open={teamPanelOpen}
          onClose={() => setTeamPanelOpen(false)}
        />
      </div>
      </div>
    </div>
  );
}
